import { useState, cloneElement, isValidElement, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal,
  FileText,
  CalendarCheck2,
  CalendarRange,
  CalendarX2,
  Wrench,
  Phone,
  Mail,
  ClipboardList,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useIsDesktop } from "@/components/ui/responsive-dialog";
import { KindIcon } from "@/components/Badges";
import { KIND_LABEL } from "@/lib/calendar";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const reportUrlFor = (e) =>
  `/rapportar/ny-service?machine=${e.machine_id}${e.kind === "annual" ? "&kontroll=1" : ""}`;

/**
 * Ekstra handlingar som ikkje treng prop-drilling gjennom alle kalendervisningane.
 * Kalender.js legg inn { onControl } her; EventActions les det ut.
 */
const CalendarActionsContext = createContext({});
export const CalendarActionsProvider = ({ value, children }) => (
  <CalendarActionsContext.Provider value={value || {}}>{children}</CalendarActionsContext.Provider>
);
const useCalendarActions = () => useContext(CalendarActionsContext) || {};

/** Menyinnhaldet (utan trigger) – slik at det kan gjenbrukast med eigne triggerar/tooltips. */
export const EventActionsItems = ({ e, onPlan, onMove, onUnplan }) => {
  const navigate = useNavigate();
  const { onControl } = useCalendarActions();
  const phone = e.customer_phone ? String(e.customer_phone).replace(/\s+/g, "") : null;
  const isAnnual = e.kind === "annual";
  return (
    <>
      <DropdownMenuLabel className="pb-1">
        <div className="truncate text-sm font-semibold">{e.label}</div>
        <div className="text-xs font-normal text-muted-foreground">
          {KIND_LABEL[e.kind]} · frist {formatDate(e.date)}
          {e.planned_date ? ` · planlagt ${formatDate(e.planned_date)}` : ""}
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {isAnnual && onControl && (
        <DropdownMenuItem className="cursor-pointer gap-2.5 py-2.5" onClick={() => onControl(e)} data-testid="action-register-control">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]"><ShieldCheck className="h-4 w-4" /></span>
          <div>
            <div className="font-semibold">Registrer årskontroll</div>
            <div className="text-xs text-muted-foreground">Utført av kontrollorgan – legg ved rapport</div>
          </div>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem className="cursor-pointer gap-2.5 py-2.5" onClick={() => navigate(reportUrlFor(e))} data-testid="action-new-report">
        <span className={cn("flex h-8 w-8 items-center justify-center rounded-md", isAnnual && onControl ? "bg-muted" : "bg-primary text-ink")}><FileText className="h-4 w-4" /></span>
        <div>
          <div className="font-semibold">Lag servicerapport</div>
          <div className="text-xs text-muted-foreground">{isAnnual ? "Årskontroll førehandsvald" : "Maskin førehandsvald"}</div>
        </div>
      </DropdownMenuItem>
      <DropdownMenuItem className="cursor-pointer gap-2.5 py-2.5" onClick={() => navigate(`/rapportar/ny-jobb?machine=${e.machine_id}`)} data-testid="action-new-jobb">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted"><ClipboardList className="h-4 w-4" /></span>
        <div className="font-medium">Lag jobbrapport</div>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="cursor-pointer gap-2.5 py-2.5" onClick={() => onPlan?.(e)} data-testid="action-plan">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted"><CalendarCheck2 className="h-4 w-4" /></span>
        <div>
          <div className="font-medium">{e.planned_date ? "Endre planlagt dato" : "Planlegg dato"}</div>
          {e.planned_date && <div className="text-xs text-muted-foreground">Planlagt {formatDate(e.planned_date)}</div>}
        </div>
      </DropdownMenuItem>
      {e.planned_date && (
        <DropdownMenuItem className="cursor-pointer gap-2.5 py-2.5" onClick={() => onUnplan?.(e)} data-testid="action-unplan">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted"><CalendarX2 className="h-4 w-4" /></span>
          <div className="font-medium">Fjern planlagt dato</div>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem className="cursor-pointer gap-2.5 py-2.5" onClick={() => onMove?.(e)} data-testid="action-move">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted"><CalendarRange className="h-4 w-4" /></span>
        <div className="font-medium">Flytt frist</div>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="cursor-pointer gap-2.5 py-2" onClick={() => navigate(`/maskiner/${e.machine_id}`)} data-testid="action-open-machine">
        <Wrench className="h-4 w-4 text-muted-foreground" /> Opne maskin
      </DropdownMenuItem>
      {phone && (
        <DropdownMenuItem asChild className="cursor-pointer gap-2.5 py-2" data-testid="action-call">
          <a href={`tel:${phone}`}><Phone className="h-4 w-4 text-muted-foreground" /> Ring {e.customer_name}</a>
        </DropdownMenuItem>
      )}
      {e.customer_email && (
        <DropdownMenuItem asChild className="cursor-pointer gap-2.5 py-2" data-testid="action-email">
          <a href={`mailto:${e.customer_email}`}><Mail className="h-4 w-4 text-muted-foreground" /> Send e-post</a>
        </DropdownMenuItem>
      )}
    </>
  );
};

/* ---------- Mobile: action sheet (bottom drawer) ---------- */

const SheetRow = ({ icon: Icon, label, desc, onClick, href, testid, primary, subtle }) => {
  const Comp = href ? "a" : "button";
  return (
    <Comp
      type={href ? undefined : "button"}
      href={href}
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "pressable flex w-full items-center gap-3 rounded-[14px] px-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
        subtle ? "min-h-[48px]" : "min-h-[60px]"
      )}
    >
      <span className={cn("grid shrink-0 place-items-center rounded-xl", subtle ? "h-9 w-9 bg-transparent text-muted-foreground" : "h-11 w-11", !subtle && (primary ? "bg-primary text-ink" : "bg-muted"))}>
        <Icon className={subtle ? "h-4 w-4" : "h-5 w-5"} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate", subtle ? "text-sm font-medium" : "text-[15px] font-semibold")}>{label}</span>
        {desc && <span className="block truncate text-xs text-muted-foreground">{desc}</span>}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Comp>
  );
};

const MobileEventActions = ({ e, open, onOpenChange, onPlan, onMove, onUnplan }) => {
  const navigate = useNavigate();
  const { onControl } = useCalendarActions();
  const isAnnual = e.kind === "annual";
  const phone = e.customer_phone ? String(e.customer_phone).replace(/\s+/g, "") : null;
  const run = (fn) => () => {
    onOpenChange(false);
    // let the drawer start closing before the next surface opens
    setTimeout(fn, 60);
  };
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid={`calendar-event-actions-${e.machine_id}-${e.kind}`}>
        <DrawerHeader className="flex flex-row items-center gap-3">
          <KindIcon kind={e.kind} />
          <span className="min-w-0">
            <DrawerTitle className="truncate text-base">{e.label}</DrawerTitle>
            <DrawerDescription className="line-clamp-2 text-xs">
              {KIND_LABEL[e.kind]} · {e.customer_name || "—"} · frist {formatDate(e.date)}
              {e.planned_date ? ` · planlagt ${formatDate(e.planned_date)}` : ""}
            </DrawerDescription>
          </span>
        </DrawerHeader>
        <DrawerBody className="space-y-0.5 pb-[calc(var(--safe-bottom)+16px)]">
          {isAnnual && onControl && (
            <SheetRow primary icon={ShieldCheck} label="Registrer årskontroll" desc="Utført av kontrollorgan – legg ved rapport" onClick={run(() => onControl(e))} testid="action-register-control" />
          )}
          <SheetRow primary={!(isAnnual && onControl)} icon={FileText} label="Lag servicerapport" desc={isAnnual ? "Årskontroll førehandsvald" : "Maskin førehandsvald"} onClick={run(() => navigate(reportUrlFor(e)))} testid="action-new-report" />
          <SheetRow icon={ClipboardList} label="Lag jobbrapport" onClick={run(() => navigate(`/rapportar/ny-jobb?machine=${e.machine_id}`))} testid="action-new-jobb" />
          <div className="my-1 border-t" />
          <SheetRow icon={CalendarCheck2} label={e.planned_date ? "Endre planlagt dato" : "Planlegg dato"} desc={e.planned_date ? `Planlagt ${formatDate(e.planned_date)}` : "Fristen står uendra"} onClick={run(() => onPlan?.(e))} testid="action-plan" />
          {e.planned_date && <SheetRow icon={CalendarX2} label="Fjern planlagt dato" onClick={run(() => onUnplan?.(e))} testid="action-unplan" />}
          <SheetRow icon={CalendarRange} label="Flytt frist" desc="Endringa blir logga" onClick={run(() => onMove?.(e))} testid="action-move" />
          <div className="my-1 border-t" />
          <SheetRow subtle icon={Wrench} label="Opne maskin" onClick={run(() => navigate(`/maskiner/${e.machine_id}`))} testid="action-open-machine" />
          {phone && <SheetRow subtle icon={Phone} label={`Ring ${e.customer_name}`} href={`tel:${phone}`} onClick={() => onOpenChange(false)} testid="action-call" />}
          {e.customer_email && <SheetRow subtle icon={Mail} label="Send e-post" href={`mailto:${e.customer_email}`} onClick={() => onOpenChange(false)} testid="action-email" />}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

/**
 * Handlingsmeny for ei frist. Desktop: dropdown. Mobil: bottom action sheet.
 * Bruk `trigger` for eigen knapp, elles «⋯».
 */
export const EventActionsMenu = ({ e, onPlan, onMove, onUnplan, trigger, align = "end" }) => {
  const desktop = useIsDesktop();
  const [open, setOpen] = useState(false);

  const defaultTrigger = (
    <Button
      variant="ghost"
      size="icon"
      className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
      aria-label="Handlingar"
      data-testid={`calendar-event-menu-${e.machine_id}-${e.kind}`}
    >
      <MoreHorizontal className="h-5 w-5" />
    </Button>
  );

  if (desktop) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger || defaultTrigger}</DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-64 p-1.5" data-testid={`calendar-event-actions-${e.machine_id}-${e.kind}`}>
          <EventActionsItems e={e} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const t = trigger || defaultTrigger;
  const mobileTrigger = isValidElement(t)
    ? cloneElement(t, {
        onClick: (ev) => {
          t.props.onClick?.(ev);
          ev.stopPropagation?.();
          setOpen(true);
        },
        "aria-haspopup": "dialog",
        "aria-expanded": open,
      })
    : t;

  return (
    <>
      {mobileTrigger}
      <MobileEventActions e={e} open={open} onOpenChange={setOpen} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
    </>
  );
};
